import { ReactElement, useRef } from "react"

import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import useMediaQuery from "$/hooks/use-media-query"

import {
    Button,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { buildCssClassName } from "@/3514/utils"
import { MAX_WIDTH_MOBILE_MEDIA, ParticipantUrls } from "@/constants"
import { BetaStatusTag } from "@/shared/tags/BetaStatusTag"
import { Tooltip } from "@/shared/tooltip/Tooltip"
import { Chevron, WiserLogo } from "@/svgs"

import { useSimulabContext } from "../context/SimulabContextProvider"
import { FeedbackTypes } from "../models"

import "./styles.scss"

type Props = {
    withFeedbackReport?: boolean
    withTooltip?: boolean
    statusTagVariant?: "outlined" | "primary"
    showCoacheesLink?: boolean
    showPracticeLink?: boolean
    showLogoOnMobile?: boolean
    handleOnClickShowPracticeLink?(): void
    renderAssignmentSummary?(): ReactElement
}

export const SimulabHeader = ({
    withFeedbackReport = false,
    statusTagVariant = "primary",
    withTooltip = false,
    showCoacheesLink = false,
    showPracticeLink = false,
    showLogoOnMobile = false,
    handleOnClickShowPracticeLink = undefined,
    renderAssignmentSummary = undefined
}: Props) => {
    const { t } = useTranslation()
    const buttonRef = useRef(null)
    const isMobile = useMediaQuery(MAX_WIDTH_MOBILE_MEDIA)
    const { setReportData } = useSimulabContext()

    const onBetaStatusClick = () => {
        if (withFeedbackReport) {
            setReportData({ type: FeedbackTypes.PRACTICE_LAB_GENERAL })
        }
    }

    return (
        <>
            <div className={buildCssClassName("simulab-header", { "with-link": showCoacheesLink || showPracticeLink })}>
                {showCoacheesLink && (
                    <Link to={ParticipantUrls.COACHEES} className="d-flex align-items-center">
                        <Chevron position="left" className="mr-1" /> <span>{t("Back to Coachees")}</span>
                    </Link>
                )}
                {showPracticeLink && (
                    <div onClick={handleOnClickShowPracticeLink} className="show-practice-link">
                        <Chevron position="left" className="mr-1" />{" "}
                        <span>{t("chapter.trackPractice.simulabChat.backToPractice")}</span>
                    </div>
                )}
                {isMobile && !showLogoOnMobile && <WiserLogo width={26} height={34} />}
                <div
                    className={buildCssClassName("flex items-center", {
                        "mx-auto": showCoacheesLink || showPracticeLink
                    })}
                >
                    <Typography
                        text="Wiser SimuLab"
                        className="simulab-header__text mb-0 max-md:ml-[20px] mr-[15px]"
                        tag={ETypographyTag.Heading1}
                        color={ETypographyColor.Black}
                        weight={ETypographyWeight.Medium}
                        size={ETypographySize.Heading1}
                        family={ETypographyFamily.DmSerif}
                    />
                    <Button isContainer className="p-0" onClick={onBetaStatusClick} {...{ ref: buttonRef }}>
                        <BetaStatusTag
                            size={isMobile ? "s" : "m"}
                            variant={statusTagVariant}
                            className="cursor-pointer"
                        />
                    </Button>
                </div>
                {withTooltip && (
                    <Tooltip targetRef={buttonRef} position="bottom-end" offset={{ x: 0, y: 5 }}>
                        <span className="color-gray fs-12 font-weight-normal">{t("Submit beta feedback")}</span>
                    </Tooltip>
                )}
            </div>
            {renderAssignmentSummary?.()}
        </>
    )
}
